import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { map, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermissionService, PERMISSION_NAMES } from '../services/permission.service';

/**
 * Route to permission name mapping (uses names from GET /api/users/me/permissions).
 */
const routePermissionNames: Record<string, string> = {
  '/config': PERMISSION_NAMES.SETTINGS_UPDATE,
  '/abuse': PERMISSION_NAMES.CASE_READ,
  '/monitor': PERMISSION_NAMES.DOMAIN_VIEW,
  '/email': PERMISSION_NAMES.EMAIL_READ,
  '/dashboard': PERMISSION_NAMES.DASH_READ,
  '/audit': PERMISSION_NAMES.AUDIT_READ,
};

/**
 * Get path only from URL (strip query params and hash) for permission lookup.
 */
function getPathOnly(url: string): string {
  const withoutHash = url.split('#')[0];
  const path = withoutHash.split('?')[0];
  return path || url;
}

/**
 * Get required permission name for a route.
 */
function getRequiredPermissionName(url: string): string | null {
  const path = getPathOnly(url);
  if (routePermissionNames[path]) {
    return routePermissionNames[path];
  }
  if (path.startsWith('/abuse/')) {
    return PERMISSION_NAMES.CASE_READ;
  }
  if (path.startsWith('/monitor/')) {
    return PERMISSION_NAMES.DOMAIN_VIEW;
  }
  if (path.startsWith('/email/')) {
    return PERMISSION_NAMES.EMAIL_READ;
  }
  if (path.startsWith('/audit/')) {
    return PERMISSION_NAMES.AUDIT_READ;
  }
  if (path.startsWith('/resource-access-management/roles/')) {
    return PERMISSION_NAMES.ROLES_READ;
  }
  if (path.startsWith('/resource-access-management/users/')) {
    return PERMISSION_NAMES.USERS_READ;
  }
  return null;
}

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const currentPath = getPathOnly(state.url);
  const isResetRequiredRoute = currentPath.startsWith('/change-password-required');

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(
      ['/signin'],
      { queryParams: { returnUrl: state.url } }
    );
  }

  if (authService.isPasswordResetRequired() && !isResetRequiredRoute) {
    return router.createUrlTree(['/change-password-required']);
  }

  // Root users bypass all permission checks
  if (authService.isRoot()) {
    return true;
  }

  // Check if route requires a specific permission (by name from GET /api/users/me/permissions)
  const requiredPermissionName = getRequiredPermissionName(state.url);

  if (requiredPermissionName) {
    const cachedNames = permissionService.getCachedPermissions();
    const requiredUpper = requiredPermissionName.toUpperCase();
    const hasPermissionFromCache = cachedNames.length > 0 &&
      cachedNames.some((p) => p.toUpperCase() === requiredUpper);

    // If cache says we have the permission, allow (and do root check if needed)
    if (hasPermissionFromCache) {
      const requiresAdmin = route.data?.['requiresAdminAccess'] as boolean;
      if (requiresAdmin && !authService.isRoot()) {
        return router.createUrlTree(['/dashboard']);
      }
      return true;
    }

    // Cache missing or says no permission: load fresh permissions once before redirecting.
    // This avoids 403 when cache is stale or was populated before role/permission updates.
    return permissionService.loadUserPermissions(true).pipe(
      switchMap(() => permissionService.hasPermissionAsync(requiredPermissionName)),
      map((hasPermission) => {
        if (!hasPermission) {
          return router.createUrlTree(['/noaccess']);
        }
        const requiresAdmin = route.data?.['requiresAdminAccess'] as boolean;
        if (requiresAdmin && !authService.isRoot()) {
          return router.createUrlTree(['/dashboard']);
        }
        return true;
      }),
      catchError(() => of(router.createUrlTree(['/noaccess'])))
    );
  }

  // Legacy access check for routes without permission mapping
  if (!authService.isAccessible(state.url)) {
    return router.createUrlTree(['/noaccess']);
  }

  const requiresAdmin = route.data?.['requiresAdminAccess'] as boolean;

  // Tenant management and similar routes require root (isRoot), not role name or isAdmin
  if (requiresAdmin && !authService.isRoot()) {
    return router.createUrlTree(['/dashboard']);
  }

  return true;
};
